<template>

    <div class="progress-monitor-preview">

        <tool-output-page :page-number="1"
                          :page-count="3">

            <!-- step 1 -->

            <p>
                This data-collection plan is for
                <span class="boilerplate-slot">{{ getDataValueOrFiller('studentName') }}</span>
                <span v-if="notEmpty('schoolName')">
                    of
                    <span class="boilerplate-slot">{{ getDataValueOrFiller('schoolName') }}</span></span>.

                <span v-if="notEmpty('adultName')">
                    <span class="boilerplate-slot">{{ getDataValueOrFiller('adultName') }}</span>
                    created the plan on
                    <span class="boilerplate-slot">{{ getDataValueOrFiller('todayDate') | moment('dddd MMMM D, YYYY') }}</span>.
                </span>

                <span v-if="notEmpty('collectorName')">
                    Person(s) collecting data for this plan include:
                    <span class="boilerplate-slot">{{ getDataValueOrFiller('collectorName') }}</span>.
                </span>
            </p>

            <p>
                The plan is designed to track this student intervention target:
                <span class="boilerplate-slot">{{ getDataValueOrFiller('interventionTarget') }}</span>
            </p>

            <!-- step 2 -->

            <h5>Data Collection:</h5>
            <p>
                Student progress during this intervention will be monitored using the data collection method
                <span class="boilerplate-slot">{{ getDataValueOrFiller('formatTitle') }}.</span>
            </p>
            <p>
                The lowest possible rating on this measure is
                <span class="boilerplate-slot">{{ getDataValueOrFiller('formatMin') }}</span>
                and the highest possible rating is
                <span class="boilerplate-slot">{{ getDataValueOrFiller('formatMax') }}</span>.
            </p>

            <!-- step 3 -->

            <h5>Intervention Length:</h5>
            <p>
                The intervention is scheduled to begin on
                <span class="boilerplate-slot">{{ getDataValueOrFiller('startDate') | moment('dddd MMMM D, YYYY') }}</span>
                and to end on
                <span class="boilerplate-slot">{{ getDataValueOrFiller('endDate') | moment('dddd MMMM D, YYYY') }}</span>.
            </p>
            <p v-if="notEmpty('startDate') && notEmpty('endDate')">
                It will last for a total of
                <span class="boilerplate-slot">{{ getDurationWeeks() }}</span>
                calendar weeks.
            </p>

            <!-- step 4 -->

            <h5>Baseline:</h5>
            <p>
                The student’s baseline performance on
                <span class="boilerplate-slot">{{ getDataValueOrFiller('formatTitle') }}</span>
                is
                <span class="boilerplate-slot">{{ getDataValueOrFiller('averageRating') }}</span>
                out of a possible
                <span class="boilerplate-slot">{{ getDataValueOrFiller('formatMax') }}</span>.
            </p>
            <p v-if="notEmpty('averageRating') && notEmpty('dataPoints')">
                This baseline was calculated using
                <span class="boilerplate-slot">{{ getDataValueOrFiller('dataPoints') }}</span>
                rating(s).
            </p>

            <!-- step 5 -->

            <h5>Outcome Goal:</h5>
            <p>
                The goal is for the student to attain a rating of at least
                <span class="boilerplate-slot">{{ getDataValueOrFiller('goalMinimum') }}</span>
                on
                <span class="boilerplate-slot">{{ getDataValueOrFiller('formatTitle') }}</span>
                at the end of the intervention period,
                <span class="boilerplate-slot">{{ getDataValueOrFiller('endDate') | moment('dddd MMMM D, YYYY') }}</span>.
            </p>

            <p v-if="docData.goalOption == 2">
                The student's performance will
                be calculated by calculating the median average using the last
                <span class="boilerplate-slot">{{ getDataValueOrFiller('goalDataPoints') }}</span>
                data points.
            </p>

            <p v-if="docData.goalOption == 3">
                The student's performance will
                be calculated by calculating the mean average using the last
                <span class="boilerplate-slot">{{ getDataValueOrFiller('goalDataPoints') }}</span>
                data points.
            </p>

        </tool-output-page>


        <tool-output-page :page-number="2"
                          :page-count="3">

            <h3>Data Recording Form</h3>
            <table class="table">
                <tr>
                    <th>Student:</th>
                    <td>{{ docData.studentName }}</td>
                    <th>Data Collectors:</th>
                    <td>{{ docData.collectorName }}</td>
                    <th>Monitoring Period:</th>
                    <td>
                        <!--                            {{ docData.startDate | date:'dddd MMMM D, YYYY' }}-->
                        to
                        <!--                            {{ docData.endDate | date:'dddd MMMM D, YYYY' }}-->
                    </td>
                </tr>
            </table>

            <!-- NB: cloudformatter doesn't work with floats, including bootstrap grid, so we use a plain ol' table for layout.
                 It also does weird things with cell widths, so we need to tweak those. -->
            <table class="data-input-table">
                <tr>
                    <td>
                        <table class="table table-striped table-condensed">
                            <tr v-for="i in getRange(1, 16)">
                                <th>{{ i }}. Date:</th>
                                <td style="min-width:100px;"></td>
                                <th>Obsv:</th>
                                <td style="min-width:100px;"></td>
                            </tr>
                        </table>
                    </td>
                    <td>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                    </td>
                    <td>
                        <table class="table table-striped table-condensed">
                            <tr v-for="i in getRange(16, 31)">
                                <th>{{ i }}. Date:</th>
                                <td style="min-width:100px;"></td>
                                <th>Obsv:</th>
                                <td style="min-width:100px;"></td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </table>

        </tool-output-page>

        <tool-output-page :page-number="3"
                          :page-count="3"
                          v-if="docData">

            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" class="recording-chart">

                <!-- chart title -->
                <text text-anchor="middle"
                      font-family="sans-serif"
                      :font-size="chart.largeFontSize"
                      :x="chart.width / 2"
                      :y="chart.largeFontSize"
                      stroke-width="0" stroke="#000000" fill="#000000">
                    Progress-Monitoring Graph: {{ docData.studentName }}
                </text>

                <!-- y axis label -->
                <!--                          ng-attr-transform="translate({{ chart.largeFontSize - 20 }} {{ chart.gridTop + chart.cellWidth * 2 }}) rotate(-90)"-->
                <text text-anchor="end"
                      font-family="sans-serif" :font-size="chart.mediumFontSize"
                      :x="0"
                      :y="20"
                      stroke-width="0" stroke="#000000" fill="#000000">
                    {{ docData.formatTitle }}
                </text>

                <!-- grid lines horizontal -->
                <g v-for="i in chart.scores">
                    <line :x1="chart.gridLeft"
                          :x2="chart.gridRight"
                          :y1="chart.gridTop + (docData.formatMax - i + 1) * chart.cellHeight"
                          :y2="chart.gridTop + (docData.formatMax - i + 1) * chart.cellHeight"
                          stroke-width="1" stroke="#cccccc">
                    </line>
                </g>

                <!-- score labels -->
                <g v-for="i in chart.scores">
                    <text text-anchor="middle" font-family="sans-serif"
                          :font-size="chart.smallFontSize"
                          :x="chart.gridLeft - chart.cellWidth"
                          :y="chart.gridTop + (docData.formatMax - i + 1) * chart.cellHeight + 3"
                          stroke-width="0" stroke="#000000" fill="#000000">
                        {{ i }}
                    </text>
                </g>

                <!-- grid lines vertical -->
                <g>
                    <line v-for="day in chart.days"
                          v-if="!day.isPadding"
                          :x1="chart.gridLeft + day.index * chart.cellWidth"
                          :x2="chart.gridLeft + day.index * chart.cellWidth"
                          :y1="chart.gridTop"
                          :y2="chart.gridTop + (chart.scores.length + 1) * chart.cellHeight"
                          stroke-width="1" stroke="#cccccc">
                    </line>
                </g>

                <!-- grid frame -->
                <g>
                    <rect :x="chart.gridLeft"
                          :y="chart.gridTop"
                          :width="chart.gridRight - chart.gridLeft"
                          :height="(chart.scores.length + 1) * chart.cellHeight"
                          stroke-width="2" stroke="#cccccc" fill="transparent">
                    </rect>
                </g>

                <!-- average rating line horizontal - NB: for some reason this does not show in PDF export if using color=000000 ?? -->
                <g>
                    <line :x1="chart.gridLeft"
                          :x2="chart.gridRight"
                          :y1="chart.gridTop + (docData.formatMax - docData.averageRating + 1) * chart.cellHeight"
                          :y2="chart.gridTop + (docData.formatMax - docData.averageRating + 1) * chart.cellHeight"
                          stroke-width="3" stroke="#010000" stroke-dasharray="10 10">
                    </line>
                </g>

                <!-- average rating label -->
                <g>
                    <text text-anchor="left" font-family="sans-serif"
                          :font-size="chart.mediumFontSize"
                          :x="chart.gridLeft + chart.cellHeight / 2"
                          :y="chart.gridTop + (docData.formatMax - docData.averageRating + 1) * chart.cellHeight + chart.smallFontSize * 1.5"
                          stroke-width="0" stroke="#000000" fill="#000000">
                        Average Rating
                    </text>
                </g>

                <!-- goal line horizontal -->
                <g v-if="docData.goalMinimum">
                    <line :x1="chart.gridLeft"
                          :x2="chart.gridRight"
                          :y1="chart.gridTop + (docData.formatMax - docData.goalMinimum + 1) * chart.cellHeight"
                          :y2="chart.gridTop + (docData.formatMax - docData.goalMinimum + 1) * chart.cellHeight"
                          stroke-width="3" stroke="#008000" stroke-dasharray="5 5">
                    </line>
                </g>

                <!-- goal label -->
                <g v-if="docData.goalMinimum">
                    <text text-anchor="right" font-family="sans-serif"
                          :font-size="chart.mediumFontSize"
                          :x="chart.gridRight - chart.cellHeight"
                          :y="chart.gridTop + (docData.formatMax - docData.goalMinimum + 1) * chart.cellHeight + chart.smallFontSize * 1.5"
                          stroke-width="0" stroke="#008000" fill="#008000">
                        Goal
                    </text>
                </g>

                <!-- days of week -->
                <g>
                    <text v-for="day in chart.days"
                          v-if="!day.isPadding"
                          text-anchor="middle" font-family="sans-serif"
                          :font-size="chart.smallFontSize"
                          :x="chart.gridLeft + day.index * chart.cellWidth"
                          :y="chart.gridTop + (chart.scores.length + 2) * chart.cellHeight"
                          stroke-width="0" stroke="#000000" fill="#000000">
                        {{ day.weekday }}
                    </text>
                </g>

                <!-- week numbers -->
                <g>
                    <text v-for="day in chart.days"
                          v-if="day.weekday == 'We'"
                          text-anchor="middle" font-family="sans-serif"
                          :font-size="chart.smallFontSize"
                          :x="chart.gridLeft + day.index * chart.cellWidth"
                          :y="chart.gridTop + (chart.scores.length + 2.5) * chart.cellHeight"
                          stroke-width="0" stroke="#000000" fill="#000000">
                        Week {{ day.week }}
                    </text>
                </g>

                <!-- Monday dates -->
                <g>
                    <text v-for="day in chart.days"
                          v-if="day.weekday == 'Mo'"
                          text-anchor="middle" font-family="sans-serif"
                          :font-size="chart.smallFontSize"
                          :x="chart.gridLeft + day.index * chart.cellWidth + chart.cellWidth * 2"
                          :y="chart.gridTop + (chart.scores.length + 3) * chart.cellHeight"
                          stroke-width="0" stroke="#000000" fill="#000000">
                        Mo: {{ day.date }}
                    </text>
                </g>

                <!-- Friday dates -->
                <g>
                    <text v-for="day in chart.days"
                          v-if="day.weekday == 'Fr'"
                          text-anchor="middle" font-family="sans-serif"
                          :font-size="chart.smallFontSize"
                          :x="chart.gridLeft + day.index * chart.cellWidth - chart.cellWidth * 2"
                          :y="chart.gridTop + (chart.scores.length + 3.5) * chart.cellHeight"
                          stroke-width="0" stroke="#000000" fill="#000000">
                        Fr: {{ day.date }}
                    </text>
                </g>

            </svg>

        </tool-output-page>

    </div>

</template>

<script>

    import Vue from 'vue'
    import moment from 'moment'
    import ToolMixin from '@/providers/ToolMixin'

    export default Vue.extend({
        name: "ProgressMonitorPreview",
        mixins: [ToolMixin],
        data() {
            // NB: some of these coordinates need to agree with the CSS for .recording-chart
            // and some of them get recalculated below based on the size of the dataset
            return {
                chart: {
                    width: 800,
                    height: 630,
                    titleHeight: 40,
                    gridLeft: 100,
                    gridRight: 800,
                    gridTop: 40,
                    cellWidth: 20,
                    cellHeight: 20,
                    smallFontSize: 10,
                    mediumFontSize: 18,
                    largeFontSize: 20,
                    days: [],
                    scores: [],
                }
            }
        },
        methods: {
            getDataValueOrFiller(key) {
                const data = this.docData
                if (data) {
                    var val = data[key]
                    // console.log('val', val);
                    if (val === undefined || val === null || val === '') {
                        return '...'
                    } else {
                        return val
                    }
                } else {
                    return null
                }
            },
            notEmpty(key) {
                const data = this.docData
                if (data) {
                    var val = data[key]
                    return val !== undefined && val !== null && val !== ''
                } else {
                    return false
                }
            },
            getDurationWeeks() {
                const data = this.docData
                if (data && data.startDate && data.endDate) {
                    const days = moment.duration(moment(data.endDate).valueOf() - moment(data.startDate).valueOf()).asDays()
                    const weeks = days / 7
                    const f0 = _.round(days / 7, 0)
                    const f1 = _.round(days / 7, 1)
                    if (parseFloat(f0) == parseFloat(f1)) {
                        return f0
                    } else {
                        return f1
                    }
                } else {
                    return '...'
                }
            },
            // generate an array of ints for use with v-for
            getRange(start, stop) {
                return _.range(start, stop);
            },

            updateChart() {
                if (!this.docData.startDate || !this.docData.endDate) {
                    this.chart.days = [];
                } else {
                    const days = [];
                    const start = moment(this.docData.startDate);
                    const end = moment(this.docData.endDate);

                    // pad to previous Sunday
                    var paddedStart = start.clone();
                    while (paddedStart.format('dd') != 'Su') {
                        paddedStart.add(-1, 'd');
                    }

                    // pad to following Saturday
                    let paddedEnd = end.clone();
                    while (paddedEnd.format('dd') != 'Su') {
                        paddedEnd.add(1, 'd');
                    }

                    const day = paddedStart.clone();

                    // build array of days
                    let i = 1;
                    let week = 1;
                    while (true) {
                        let weekday = day.format('dd');
                        if (weekday != 'Sa' && weekday != 'Su') {
                            days.push({
                                index: i,
                                date: day.format('MMM d, YYYY'),
                                weekday: weekday,
                                week: week,
                                isPadding: day.format('X') < start.format('X') || day.format('X') > end.format('X'),
                                x: this.chart.gridLeft + i * this.chart.cellWidth
                            });
                        }
                        if (weekday == 'Su') {
                            week++;
                        }
                        if (day.format() == paddedEnd.format()) {
                            break;
                        }
                        day.add(1, 'd');
                        i++;
                    }

                    this.chart.days = days;

                    // tweak dimensions
                    this.chart.gridLeft = this.chart.largeFontSize + this.chart.smallFontSize * 6;
                    this.chart.gridTop = this.chart.largeFontSize + this.chart.smallFontSize * 2;

                    this.chart.cellWidth = (this.chart.width - this.chart.gridLeft) / i;
                    this.chart.cellHeight = (this.chart.height - this.chart.gridTop) / (this.docData.formatMax - this.docData.formatMin + 5);
                    this.chart.smallFontSize = this.chart.cellWidth / 2;

                    this.chart.scores = _.range(this.docData.formatMin, this.docData.formatMax + 1);
                }

                // self.ui.save(self);

            }
        },
        mounted() {
            this.updateChart()
        }
    })

</script>

<style lang="scss">

    .recording-form .data-input-table {
        width: 100%;
    }

    .recording-form .data-input-table th {
        font-weight: normal;
    }

    .recording-chart {
        width: 800px;
        height: 700px;
        overflow: hidden;
        position: relative;
    }

</style>

